import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import Header from '../../components/Header';
import SearchForm from '../../components/SearchForm';
import SubscriberCard from '../../components/SubscriberCard';
import suscriptorService from '../../services/suscriptorService';
import { SuscriptorResponse } from '../../types/Suscriptor';
import { Alert, Card, staggerParent, enterChild } from '../../components/ui';
import { SearchIcon } from '../../components/icons';

/** Silueta del resultado mientras se consulta, para que la página no salte. */
const ResultadoSkeleton: React.FC = () => (
  <div className="space-y-4">
    <Card className="animate-pulse p-5">
      <div className="h-12 rounded-lg bg-slate-100" />
      <div className="mt-4 h-4 w-40 rounded bg-slate-100" />
      <div className="mt-4 grid grid-cols-2 gap-2 sm:grid-cols-4">
        {[0, 1, 2, 3].map((i) => (
          <div key={i} className="h-20 rounded-xl bg-slate-100" />
        ))}
      </div>
    </Card>
    <Card className="animate-pulse p-6">
      <div className="h-4 w-44 rounded bg-slate-100" />
      <div className="mt-5 grid grid-cols-1 gap-x-8 gap-y-5 sm:grid-cols-2">
        {[0, 1, 2, 3, 4, 5].map((i) => (
          <div key={i}>
            <div className="h-2.5 w-24 rounded bg-slate-100" />
            <div className="mt-2 h-4 w-36 rounded bg-slate-100" />
          </div>
        ))}
      </div>
    </Card>
  </div>
);

const HomePage: React.FC = () => {
  const [resultado, setResultado] = useState<SuscriptorResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  /** Último documento consultado, para poder nombrarlo en el estado vacío. */
  const [consultado, setConsultado] = useState<string | null>(null);

  const buscarPorDocumento = async (tipoDoc: string, numDoc: string) => {
    setIsLoading(true);
    setError(null);
    setResultado(null);

    try {
      const respuesta = await suscriptorService.getSuscriptorByDocumento(tipoDoc, numDoc);

      if (respuesta.success) {
        setResultado(respuesta);
      } else if (!respuesta.notFound) {
        // Sólo es error si falló de verdad: el 404 se muestra como estado vacío.
        setError(respuesta.message || 'Error al consultar el suscriptor');
      }
    } catch (err) {
      setError('Error al comunicarse con el servidor');
      console.error('Error al buscar suscriptor por documento:', err);
    } finally {
      setIsLoading(false);
      setConsultado(`${tipoDoc} ${numDoc}`);
    }
  };

  const hayResultado = Boolean(resultado?.success && resultado.suscriptor);
  const sinResultados = consultado !== null && !isLoading && !error && !hayResultado;

  return (
    <div className="flex min-h-screen flex-col bg-gray-50">
      <Header />

      <main className="container mx-auto flex-grow px-4 py-8">
        <motion.div
          variants={staggerParent}
          initial="hidden"
          animate="visible"
          className="mx-auto max-w-5xl space-y-6"
        >
          <motion.div variants={enterChild}>
            <h1 className="font-poppins text-2xl font-semibold text-gappex-secondary">
              Consulta de suscriptores
            </h1>
            <p className="mt-1 text-sm text-slate-500">
              Buscá por documento para ver el plan vigente, la credencial y los descuentos que le
              corresponden.
            </p>
          </motion.div>

          <motion.div variants={enterChild}>
            <SearchForm onBuscarPorDocumento={buscarPorDocumento} isLoading={isLoading} />
          </motion.div>

          <AnimatePresence initial={false} mode="wait">
            {isLoading && (
              <motion.div
                key="cargando"
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -8 }}
                transition={{ duration: 0.2, ease: 'easeOut' }}
                aria-busy="true"
                aria-label="Consultando información del suscriptor"
              >
                <ResultadoSkeleton />
              </motion.div>
            )}

            {!isLoading && error && (
              <Alert
                key="error"
                tone="error"
                title="No se pudo completar la consulta"
                onClose={() => setError(null)}
              >
                {error}
              </Alert>
            )}

            {!isLoading && sinResultados && (
              <motion.div
                key="vacio"
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -8 }}
                transition={{ duration: 0.2, ease: 'easeOut' }}
              >
                <Card className="flex flex-col items-center gap-2 px-6 py-12 text-center">
                  <span className="text-slate-300">
                    <SearchIcon className="h-8 w-8" strokeWidth={1.5} />
                  </span>
                  <p className="text-sm font-semibold text-gappex-secondary">
                    No hay ningún suscriptor con {consultado}
                  </p>
                  <p className="max-w-sm text-xs text-slate-400">
                    Revisá el tipo de documento y que el número vaya sin puntos ni espacios.
                  </p>
                </Card>
              </motion.div>
            )}

            {!isLoading && hayResultado && resultado?.suscriptor && (
              <motion.div
                key="resultado"
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -8 }}
                transition={{ duration: 0.25, ease: [0.2, 0, 0, 1] }}
              >
                <SubscriberCard suscriptor={resultado.suscriptor} planes={resultado.planes} />
              </motion.div>
            )}
          </AnimatePresence>
        </motion.div>
      </main>

      <footer className="mt-auto bg-gappex-dark py-6 text-white">
        <div className="container mx-auto px-4 text-center">
          <p className="mb-0 text-sm">
            © {new Date().getFullYear()} Gappex — Validación de Suscriptores
          </p>
        </div>
      </footer>
    </div>
  );
};

export default HomePage;
